import React, { useEffect, useMemo, useRef, useState } from "react";
import { OGDB2 } from "../ogdb2Api";

const DAYS_PER_MONTH = 30.4375;

function toNum(x) {
  if (x === null || x === undefined || x === "") return null;
  if (typeof x === "number") return Number.isFinite(x) ? x : null;
  const n = Number(String(x).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

function fmt(n, digits = 0) {
  const v = toNum(n);
  if (v === null) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function fmtPct(n, digits = 1) {
  const v = toNum(n);
  if (v === null) return "—";
  return `${v.toFixed(digits)}%`;
}

function monthLabel(m) {
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return names[(m - 1) % 12] || String(m);
}

function basisLabel(b) {
  const map = {
    last_1: "Last month",
    last_3: "Last 3 mo avg",
    last_4: "Last 4 mo avg",
    last_6: "Last 6 mo avg",
    last_12: "Last 12 mo avg",
    sep_dec: "Sep–Dec avg",
  };
  return map[b] || b || "—";
}

function StatCard({ label, value, sub, tone = "neutral" }) {
  const toneMap = {
    neutral: "border-gray-200 bg-white",
    good: "border-emerald-200 bg-emerald-50",
    bad: "border-rose-200 bg-rose-50",
  };
  return (
    <div className={`rounded-2xl border ${toneMap[tone] || toneMap.neutral} p-3`}>
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-base font-semibold text-gray-900 mt-1">{value}</div>
      {sub ? <div className="text-[11px] text-gray-500 mt-1">{sub}</div> : null}
    </div>
  );
}

/**
 * resolvedFacts shape:
 *   resolvedFacts[code] = { assessor, actual, third_party, ba, unit }
 */
function getResolved(resolvedFacts, code, scenario) {
  if (!resolvedFacts || !code) return null;
  const obj = resolvedFacts[code];
  if (!obj) return null;
  const v = obj?.[scenario];
  return v === undefined ? null : v;
}

/**
 * Match OGDB2 basis logic (fallback chain).
 */
function getComparable3PStartRate(resolvedFacts, product, basisOverride) {
  const basisCode = product === "oil" ? "tp_start_rate_basis_oil" : "tp_start_rate_basis_gas";

  const basis =
    String(basisOverride || "").trim() ||
    String(getResolved(resolvedFacts, basisCode, "ba") || "").trim() ||
    String(getResolved(resolvedFacts, basisCode, "third_party") || "").trim() ||
    "sep_dec";

  const map =
    product === "oil"
      ? {
          sep_dec: "tp_oil_sr_sep_dec",
          last_1: "tp_oil_sr_last_1",
          last_3: "tp_oil_sr_last_3",
          last_4: "tp_oil_sr_last_4",
          last_6: "tp_oil_sr_last_6",
          last_12: "tp_oil_sr_last_12",
        }
      : {
          sep_dec: "tp_gas_sr_sep_dec",
          last_1: "tp_gas_sr_last_1",
          last_3: "tp_gas_sr_last_3",
          last_4: "tp_gas_sr_last_4",
          last_6: "tp_gas_sr_last_6",
          last_12: "tp_gas_sr_last_12",
        };

  const pick = (code) => {
    const v = toNum(getResolved(resolvedFacts, code, "third_party"));
    return v === null ? null : v;
  };

  const primary = map[basis] || map.sep_dec;

  return (
    pick(primary) ??
    pick(map.sep_dec) ??
    pick(map.last_4) ??
    pick(map.last_3) ??
    pick(map.last_1)
  );
}

/**
 * Which months should be highlighted for a given basis?
 * We treat "last_N" as the last N months of the year (ending Dec).
 */
function basisWindowMonths(basis) {
  const b = (basis || "").trim() || "sep_dec";
  if (b === "sep_dec") return [9, 10, 11, 12];
  if (b === "last_1") return [12];
  if (b === "last_3") return [10, 11, 12];
  if (b === "last_4") return [9, 10, 11, 12];
  if (b === "last_6") return [7, 8, 9, 10, 11, 12];
  if (b === "last_12") return [1,2,3,4,5,6,7,8,9,10,11,12];
  return [9, 10, 11, 12];
}

/**
 * Robust outlier detection on month-to-month % change.
 * Flags spikes/drops when:
 * - abs(pct_change) is high AND
 * - robust z-score is high
 */
function detectOutliers(series) {
  // series: [{m, y, daily}]
  // compute pct changes for months with previous value
  const changes = [];
  const changeByIdx = new Map(); // idx -> pct_change

  for (let i = 0; i < series.length; i++) {
    const cur = toNum(series[i].y);
    const prev = i > 0 ? toNum(series[i - 1].y) : null;
    if (cur == null || prev == null || prev === 0) {
      changeByIdx.set(i, null);
      continue;
    }
    const pct = ((cur - prev) / Math.abs(prev)) * 100;
    changes.push(pct);
    changeByIdx.set(i, pct);
  }

  const outlierIdx = new Set();

  // If not enough data, still do a simple pct threshold
  if (changes.length < 4) {
    for (let i = 0; i < series.length; i++) {
      const pct = changeByIdx.get(i);
      if (pct == null) continue;
      if (Math.abs(pct) >= 50) outlierIdx.add(i);
    }
    return { changeByIdx, outlierIdx };
  }

  // median
  const sorted = [...changes].sort((a, b) => a - b);
  const med = sorted.length % 2 ? sorted[(sorted.length - 1) / 2] : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;

  // MAD (median absolute deviation)
  const absDev = changes.map((c) => Math.abs(c - med)).sort((a, b) => a - b);
  const mad = absDev.length % 2 ? absDev[(absDev.length - 1) / 2] : (absDev[absDev.length / 2 - 1] + absDev[absDev.length / 2]) / 2;

  // --- Rule set ---
  // 1) Big absolute MoM move (hard threshold) -> outlier
  // 2) Robust z-score extreme -> outlier
  // 3) Early-year "cliff": if month 2 or 3 is < 20% of month 1, flag it (common in production cleanup / shut-in patterns)

  // Rule 3: early cliff
  const m1 = toNum(series[0]?.y); // Jan
  const m2 = toNum(series[1]?.y); // Feb
  const m3 = toNum(series[2]?.y); // Mar
  if (m1 != null && m1 > 0) {
    if (m2 != null && m2 / m1 <= 0.2) outlierIdx.add(1);
    if (m3 != null && m3 / m1 <= 0.2) outlierIdx.add(2);
  }

  for (let i = 0; i < series.length; i++) {
    const pct = changeByIdx.get(i);
    if (pct == null) continue;

    // Robust z-score
    const z = mad === 0 ? 0 : (0.6745 * (pct - med)) / mad;

    // Rule 1: hard move threshold
    if (Math.abs(pct) >= 60) {
      outlierIdx.add(i);
      continue;
    }

    // Rule 2: z-score threshold (slightly lower than 3 to catch big moves even when MAD is inflated)
    if (Math.abs(z) >= 2.5 && Math.abs(pct) >= 35) {
      outlierIdx.add(i);
      continue;
    }
  }

  return { changeByIdx, outlierIdx };
}

/**
 * 3-month moving average (uses available values, requires at least 2 points in the window).
 */
function movingAverage3(series) {
  const out = [];
  for (let i = 0; i < series.length; i++) {
    const window = [];
    for (let k = i - 2; k <= i; k++) {
      if (k < 0) continue;
      const v = toNum(series[k].y);
      if (v != null) window.push(v);
    }
    if (window.length >= 2) {
      out.push(window.reduce((a, b) => a + b, 0) / window.length);
    } else {
      out.push(null);
    }
  }
  return out;
}

/**
 * Futuristic SVG chart with:
 * - basis window highlight (background bands)
 * - optional 3mo MA overlay
 * - outlier markers
 * - hover selection via onHover(index)
 */
function FuturisticChart({
  title,
  subtitle,
  unitLabel,
  series, // [{xLabel, y, daily, m}]
  highlightMonths, // [1..12]
  showMA,
  outlierIdx,
  onHover,
}) {
  const svgRef = useRef(null);

  const W = 1200;
  const H = 520; // bigger so it fills the modal more
  const padL = 58;
  const padR = 26;
  const padT = 24;
  const padB = 56;

  const ys = series.map((p) => (p.y == null ? null : Number(p.y))).filter((v) => v != null);
  const maxY = Math.max(1, ...(ys.length ? ys : [1]));
  const minY = 0;

  const xStep = (W - padL - padR) / Math.max(1, series.length - 1);

  function xy(i, y) {
    const x = padL + i * xStep;
    const t = (y - minY) / (maxY - minY || 1);
    const yy = padT + (1 - t) * (H - padT - padB);
    return [x, yy];
  }

  function buildLinePath(arr) {
    let d = "";
    let started = false;
    for (let i = 0; i < arr.length; i++) {
      const y = arr[i];
      if (y == null) {
        started = false;
        continue;
      }
      const [x, yy] = xy(i, y);
      if (!started) {
        d += `M ${x} ${yy}`;
        started = true;
      } else {
        d += ` L ${x} ${yy}`;
      }
    }
    return d || `M ${padL} ${H - padB} L ${W - padR} ${H - padB}`;
  }

  function buildAreaPath(lineD, arr) {
    const baseY = H - padB;
    const firstIdx = arr.findIndex((v) => v != null);
    const lastIdx = (() => {
      for (let i = arr.length - 1; i >= 0; i--) if (arr[i] != null) return i;
      return -1;
    })();
    if (firstIdx === -1 || lastIdx === -1) {
      return `M ${padL} ${baseY} L ${W - padR} ${baseY} L ${W - padR} ${baseY} L ${padL} ${baseY} Z`;
    }
    const [x0] = xy(firstIdx, arr[firstIdx]);
    const [x1] = xy(lastIdx, arr[lastIdx]);
    return `${lineD} L ${x1} ${baseY} L ${x0} ${baseY} Z`;
  }

  const baseArr = series.map((p) => (p.y == null ? null : Number(p.y)));
  const lineD = buildLinePath(baseArr);
  const areaD = buildAreaPath(lineD, baseArr);

  const maArr = showMA ? movingAverage3(series) : null;
  const maD = showMA ? buildLinePath(maArr) : null;

  function handleMove(e) {
    if (!svgRef.current) return;

    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    // The SVG viewBox may be "letterboxed" inside the rendered rect due to preserveAspectRatio.
    // Default is xMidYMid meet, so we compute scale + offsets.
    const scale = Math.min(rect.width / W, rect.height / H);
    const viewW = W * scale;
    const viewH = H * scale;
    const offsetX = (rect.width - viewW) / 2;
    const offsetY = (rect.height - viewH) / 2;

    // Convert mouse position in pixels -> viewBox coordinates
    const xInView = (px - offsetX) / scale;
    const yInView = (py - offsetY) / scale;

    // If we're outside the actual drawn viewBox area, ignore
    if (xInView < 0 || xInView > W || yInView < 0 || yInView > H) {
      onHover?.(null);
      return;
    }

    const i = Math.round((xInView - padL) / xStep);
    const idx = Math.max(0, Math.min(series.length - 1, i));
    onHover?.(idx);
  }

  function handleLeave() {
    onHover?.(null);
  }

  // highlight bands for selected months
  const highlightSet = new Set(highlightMonths || []);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-[11px] text-gray-500">{unitLabel}</div>
      </div>

      <div className="p-3">
        <svg
		  ref={svgRef}
		  viewBox={`0 0 ${W} ${H}`}
		  preserveAspectRatio="xMidYMid meet"
		  width="100%"
		  height="520"
		  className="block"
		  onMouseMove={handleMove}
		  onMouseLeave={handleLeave}
		>
          <defs>
            <linearGradient id="areaGradX" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#111827" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#111827" stopOpacity="0.03" />
            </linearGradient>

            <filter id="glowX">
              <feGaussianBlur stdDeviation="2.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Basis window highlight bands */}
          {series.map((p, i) => {
            const monthNum = p.m;
            if (!highlightSet.has(monthNum)) return null;

            const x = padL + i * xStep;
            // make the band span to next point; for last point, use a small width
            const w = i < series.length - 1 ? xStep : xStep * 0.75;

            return (
              <rect
                key={`hl-${i}`}
                x={x - xStep * 0.5}
                y={padT}
                width={w}
                height={H - padT - padB}
                fill="#111827"
                opacity="0.035"
              />
            );
          })}

          {/* Grid */}
          {Array.from({ length: 6 }).map((_, i) => {
            const y = padT + (i * (H - padT - padB)) / 5;
            return (
              <line
                key={`gy-${i}`}
                x1={padL}
                x2={W - padR}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
            );
          })}

          {/* X labels */}
          {series.map((p, i) => {
            const x = padL + i * xStep;
            if (i % 2 === 1) return null;
            return (
              <text key={`xl-${i}`} x={x} y={H - 18} fontSize="10" fill="#6b7280" textAnchor="middle">
                {p.xLabel}
              </text>
            );
          })}

          {/* Area */}
          <path d={areaD} fill="url(#areaGradX)" />

          {/* Main line */}
          <path d={lineD} stroke="#111827" strokeWidth="6" fill="none" opacity="0.14" filter="url(#glowX)" />
          <path d={lineD} stroke="#111827" strokeWidth="2" fill="none" />

          {/* 3-mo MA overlay */}
          {showMA && maD ? (
            <>
              <path d={maD} stroke="#6b7280" strokeWidth="2" fill="none" strokeDasharray="6 5" opacity="0.95" />
            </>
          ) : null}

          {/* Points (outliers get ring) */}
          {series.map((p, i) => {
            if (p.y == null) return null;
            const [x, y] = xy(i, p.y);

            const isOut = outlierIdx?.has(i);

            return (
              <g key={`pt-${i}`}>
                {isOut ? (
                  <circle cx={x} cy={y} r="8" fill="none" stroke="#ef4444" strokeWidth="2" opacity="0.9" />
                ) : null}
                <circle cx={x} cy={y} r="3.8" fill="#111827" />
              </g>
            );
          })}
        </svg>

        <div className="text-[11px] text-gray-500 mt-2 flex flex-wrap gap-3">
          <span>Shaded bands = basis window</span>
          <span>Dashed line = 3-mo moving avg (if enabled)</span>
          <span>Red ring = spike/drop outlier</span>
        </div>
      </div>
    </div>
  );
}

export default function ProductionDetailModal({
  open,
  onClose,
  unitId,
  prodYear,
  resolvedFacts,

  // ✅ NEW: choose which tab to open on ("oil" | "gas")
  defaultTab,

  oilBasis,
  gasBasis,
  onSetOilBasis,
  onSetGasBasis,
}) {
  const [activeTab, setActiveTab] = useState("oil"); // "oil" | "gas"
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [hoverIdx, setHoverIdx] = useState(null);

  // toggle: 3-mo moving avg overlay
  const [showMA, setShowMA] = useState(true);

  useEffect(() => {
    if (!open) return;
    setActiveTab(String(defaultTab || "oil").toLowerCase() === "gas" ? "gas" : "oil");
    setHoverIdx(null);
  }, [open, defaultTab]);

  useEffect(() => {
    if (!open || !unitId || !prodYear) return;
    setBusy(true);
    setErr("");
    setItems([]);

    OGDB2.getBAIDMonthlyProduction(unitId, Number(prodYear))
      .then((res) => {
        const arr = Array.isArray(res?.data?.items) ? res.data.items : [];
        setItems(arr);
      })
      .catch((e) => setErr(e?.response?.data?.detail || e?.message || "Failed to load monthly production"))
      .finally(() => setBusy(false));
  }, [open, unitId, prodYear]);

  const baseSeries = useMemo(() => {
    const byM = new Map();
    for (const r of items || []) {
      const m = Number(r?.month);
      if (!Number.isFinite(m)) continue;
      byM.set(m, { oil: toNum(r?.oil_bbl), gas: toNum(r?.gas_mcf) });
    }

    const months = [];
    for (let m = 1; m <= 12; m++) {
      const v = byM.get(m) || {};
      months.push({ m, oil: v.oil ?? null, gas: v.gas ?? null });
    }
    return months;
  }, [items]);

  // Assessor comparable SR
  const assessorOilSR = toNum(getResolved(resolvedFacts, "comparable_oil_start_rate", "assessor"));
  const assessorGasSR = toNum(getResolved(resolvedFacts, "comparable_gas_start_rate", "assessor"));

  // 3P basis-selected SR
  const tpOilSR = toNum(getComparable3PStartRate(resolvedFacts, "oil", oilBasis));
  const tpGasSR = toNum(getComparable3PStartRate(resolvedFacts, "gas", gasBasis));

  const oilDeltaPct = useMemo(() => {
    if (assessorOilSR == null || tpOilSR == null || assessorOilSR === 0) return null;
    return ((tpOilSR - assessorOilSR) / Math.abs(assessorOilSR)) * 100;
  }, [assessorOilSR, tpOilSR]);

  const gasDeltaPct = useMemo(() => {
    if (assessorGasSR == null || tpGasSR == null || assessorGasSR === 0) return null;
    return ((tpGasSR - assessorGasSR) / Math.abs(assessorGasSR)) * 100;
  }, [assessorGasSR, tpGasSR]);

  const deltaTone = (d) => (d == null ? "neutral" : d < 0 ? "good" : d > 0 ? "bad" : "neutral");

  // Build active tab series
  const oilChartSeries = useMemo(() => {
    return baseSeries.map((r) => ({
      m: r.m,
      xLabel: monthLabel(r.m),
      y: r.oil,
      daily: r.oil == null ? null : r.oil / DAYS_PER_MONTH,
    }));
  }, [baseSeries]);

  const gasChartSeries = useMemo(() => {
    return baseSeries.map((r) => ({
      m: r.m,
      xLabel: monthLabel(r.m),
      y: r.gas,
      daily: r.gas == null ? null : r.gas / DAYS_PER_MONTH,
    }));
  }, [baseSeries]);

  const activeSeries = activeTab === "oil" ? oilChartSeries : gasChartSeries;

  // Outlier detection (per active tab)
  const { changeByIdx, outlierIdx } = useMemo(() => {
    return detectOutliers(activeSeries.map((p) => ({ m: p.m, y: p.y, daily: p.daily })));
  }, [activeSeries]);

  // Basis window highlight months
  const highlightMonths = useMemo(() => {
    const b = activeTab === "oil" ? (oilBasis || "sep_dec") : (gasBasis || "sep_dec");
    return basisWindowMonths(b);
  }, [activeTab, oilBasis, gasBasis]);

  // Hover tooltip data
  const hoverData = useMemo(() => {
    if (hoverIdx == null) return null;
    const p = activeSeries?.[hoverIdx];
    if (!p) return null;

    const mom = changeByIdx.get(hoverIdx);
    const isOut = outlierIdx.has(hoverIdx);

    return {
      month: p.xLabel,
      vol: p.y,
      daily: p.daily,
      mom,
      isOut,
    };
  }, [hoverIdx, activeSeries, changeByIdx, outlierIdx]);

  if (!open) return null;

  const tabBtn = (tab, label) => {
    const active = activeTab === tab;
    return (
      <button
        type="button"
        onClick={() => {
          setActiveTab(tab);
          setHoverIdx(null);
        }}
        className={
          "px-3 py-1.5 rounded-xl text-xs font-semibold border transition " +
          (active
            ? "bg-gray-900 text-white border-gray-900 shadow-sm"
            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50")
        }
      >
        {label}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="absolute inset-2 md:inset-6 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-gray-900">Production Detail</div>
            <div className="text-xs text-gray-500">Monthly production for {prodYear}</div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {tabBtn("oil", "Oil")}
            {tabBtn("gas", "Gas")}

            <div className="w-px h-6 bg-gray-200 mx-1 hidden md:block" />

            {/* Toggle: 3-mo MA overlay */}
            <label className="text-xs text-gray-700 flex items-center gap-2 select-none">
              <input
                type="checkbox"
                checked={showMA}
                onChange={(e) => setShowMA(e.target.checked)}
              />
              3-mo moving avg
            </label>

            {/* Basis selector only for active tab */}
            {activeTab === "oil" ? (
              <label className="text-xs text-gray-700">
                Oil SR Basis
                <select
                  className="ml-2 rounded-xl border border-gray-200 bg-white px-2 py-2 text-xs"
                  value={oilBasis || ""}
                  onChange={(e) => onSetOilBasis?.(e.target.value)}
                >
                  <option value="">(none)</option>
                  <option value="last_1">Last month</option>
                  <option value="last_3">Last 3 mo avg</option>
                  <option value="last_4">Last 4 mo avg</option>
                  <option value="last_6">Last 6 mo avg</option>
                  <option value="last_12">Last 12 mo avg</option>
                  <option value="sep_dec">Sep–Dec avg</option>
                </select>
              </label>
            ) : (
              <label className="text-xs text-gray-700">
                Gas SR Basis
                <select
                  className="ml-2 rounded-xl border border-gray-200 bg-white px-2 py-2 text-xs"
                  value={gasBasis || ""}
                  onChange={(e) => onSetGasBasis?.(e.target.value)}
                >
                  <option value="">(none)</option>
                  <option value="last_1">Last month</option>
                  <option value="last_3">Last 3 mo avg</option>
                  <option value="last_4">Last 4 mo avg</option>
                  <option value="last_6">Last 6 mo avg</option>
                  <option value="last_12">Last 12 mo avg</option>
                  <option value="sep_dec">Sep–Dec avg</option>
                </select>
              </label>
            )}

            <button className="btn" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-4 bg-gray-50">
          {err ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {String(err)}
            </div>
          ) : null}

          {busy ? <div className="text-sm text-gray-500">Loading…</div> : null}

          {/* Start Rate Cards */}
          {activeTab === "oil" ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StatCard
                label="Assessor Comparable Start Rate"
                value={`${fmt(assessorOilSR, 2)} bbl/d`}
                sub="Scenario: Assessor"
              />
              <StatCard
                label={`3P Start Rate (Basis: ${basisLabel(oilBasis || "sep_dec")})`}
                value={`${fmt(tpOilSR, 2)} bbl/d`}
                sub="Scenario: Third Party (GasSearch)"
              />
              <StatCard
                label="3P vs Assessor Delta"
                value={fmtPct(oilDeltaPct, 1)}
                sub="(3P - Assessor) / Assessor"
                tone={deltaTone(oilDeltaPct)}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StatCard
                label="Assessor Comparable Start Rate"
                value={`${fmt(assessorGasSR, 2)} mcf/d`}
                sub="Scenario: Assessor"
              />
              <StatCard
                label={`3P Start Rate (Basis: ${basisLabel(gasBasis || "sep_dec")})`}
                value={`${fmt(tpGasSR, 2)} mcf/d`}
                sub="Scenario: Third Party (GasSearch)"
              />
              <StatCard
                label="3P vs Assessor Delta"
                value={fmtPct(gasDeltaPct, 1)}
                sub="(3P - Assessor) / Assessor"
                tone={deltaTone(gasDeltaPct)}
              />
            </div>
          )}

          {/* Big chart (fills modal) */}
          <div className="relative">
            <FuturisticChart
              title={activeTab === "oil" ? "Year Trend — Oil" : "Year Trend — Gas"}
              subtitle={
                activeTab === "oil"
                  ? "Monthly oil volume (bbl) — shaded = basis window"
                  : "Monthly gas volume (mcf) — shaded = basis window"
              }
              unitLabel="Hover dots for exact values"
              series={activeSeries}
              highlightMonths={highlightMonths}
              showMA={showMA}
              outlierIdx={outlierIdx}
              onHover={(idx) => setHoverIdx(idx)}
            />

            {/* Hover tooltip */}
            {hoverData ? (
              <div className="absolute right-4 top-4 rounded-2xl border border-gray-200 bg-white/95 backdrop-blur px-3 py-2 shadow-sm">
                <div className="text-xs font-semibold text-gray-900 flex items-center gap-2">
                  {hoverData.month}
                  {hoverData.isOut ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700">
                      Outlier
                    </span>
                  ) : null}
                </div>

                <div className="text-[11px] text-gray-600 mt-1">
                  {activeTab === "oil" ? "Oil" : "Gas"} volume:{" "}
                  <span className="font-semibold text-gray-900">
                    {fmt(hoverData.vol, 0)} {activeTab === "oil" ? "bbl" : "mcf"}
                  </span>
                </div>

                <div className="text-[11px] text-gray-600 mt-0.5">
                  Implied daily:{" "}
                  <span className="font-semibold text-gray-900">
                    {fmt(hoverData.daily, 2)} {activeTab === "oil" ? "bbl/d" : "mcf/d"}
                  </span>
                </div>

                <div className="text-[11px] text-gray-600 mt-0.5">
                  MoM change:{" "}
                  <span className="font-semibold text-gray-900">
                    {hoverData.mom == null ? "—" : `${hoverData.mom.toFixed(1)}%`}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">
                Monthly Table — {activeTab === "oil" ? "Oil" : "Gas"}
              </div>
              <div className="text-[11px] text-gray-500">
                Daily = volume / {DAYS_PER_MONTH.toFixed(4)}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100 bg-gray-50">
                    <th className="py-2 px-3">Month</th>
                    <th className="py-2 px-3 text-right">{activeTab === "oil" ? "Oil (bbl)" : "Gas (mcf)"}</th>
                    <th className="py-2 px-3 text-right">
                      Implied Daily ({activeTab === "oil" ? "bbl/d" : "mcf/d"})
                    </th>
                    <th className="py-2 px-3 text-right">MoM %</th>
                    <th className="py-2 px-3 text-right">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSeries.map((r, idx) => {
                    const mom = changeByIdx.get(idx);
                    const isOut = outlierIdx.has(idx);
                    return (
                      <tr
                        key={r.xLabel}
                        className={
                          "border-b border-gray-50 " + (idx % 2 === 0 ? "bg-white" : "bg-gray-50/40")
                        }
                      >
                        <td className="py-2 px-3 font-medium text-gray-900">{r.xLabel}</td>
                        <td className="py-2 px-3 text-right">{fmt(r.y, 0)}</td>
                        <td className="py-2 px-3 text-right">{fmt(r.daily, 2)}</td>
                        <td className="py-2 px-3 text-right">{mom == null ? "—" : `${mom.toFixed(1)}%`}</td>
                        <td className="py-2 px-3 text-right">
                          {isOut ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700">
                              Outlier
                            </span>
                          ) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[11px] text-gray-500">
            This modal is intentionally “near full screen” so it feels like a dedicated workspace.
          </div>
        </div>
      </div>
    </div>
  );
}
